/**
 * Widget API client — thin fetch wrapper against /api/widget/:slug/*.
 *
 * Resolves the base URL from a data attribute or falls back to the script
 * origin. Each instance is bound to one tenant slug.
 */

import { bootstrapDiffers, cacheKey, getCached, isStale, setCached } from './bootstrap-cache';
import type { AvailableSlot, BootstrapResponse, Booking, Cart, DepositInfo, ShopOrder } from './types';

export class WidgetApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Recovery copy for a checkout failure that routes the customer back to
 * the Time step, or `null` for one that dead-ends on an error screen.
 *
 * Single source of truth on purpose. This decision used to live as two
 * inline `if` branches in `confirmBooking`, which was fine until the
 * failure had to be *reported* as well as handled: the report needs to
 * know whether the customer got a way forward, and a second copy of the
 * rule is a copy that drifts the next time a code is added.
 *
 *   - CART_EXPIRED: the 30-min hold lapsed; the server demoted the cart
 *     (slot cleared, service + contact intact). HoldStrip's countdown
 *     usually catches this first, but background tabs throttle timers,
 *     so the server rejection is the backstop (live: Lotus 2026-06-10).
 *     The server's message is already customer-appropriate here.
 *   - SLOT_UNAVAILABLE: the provider rejected the slot at booking time —
 *     taken by someone else, or the salon's schedule moved (live: KC
 *     Beauty 2026-06-12, Meevo "Conflicts with the employee's available
 *     time"). Retrying the SAME held slot can never succeed, so a
 *     re-pick is the only way forward, and the provider's wording is
 *     too internal to show — override it.
 */
export function checkoutRecoveryMessage(
  code: string | undefined,
  serverMessage: string,
): string | null {
  if (code === 'CART_EXPIRED') {
    // Fall back when the server sends a blank message. The old inline
    // branch passed `e.message` through unguarded, so an empty body put
    // the customer back on the Time step with an empty banner and no
    // explanation of why their slot vanished.
    return serverMessage.trim() || 'Your held time expired — please pick another time.';
  }
  if (code === 'SLOT_UNAVAILABLE') {
    return 'That time is no longer available — please pick another time.';
  }
  return null;
}

/**
 * The banner on the Time step after a lost slot, once the fresh quote is
 * in. P5.2: a customer who has ALREADY PAID is sent back here too (the
 * server keeps their payment and books the new time on it), and the one
 * thing they must hear is that their money is safe and they will not pay
 * again — the generic "pick another time" reads like "start over and pay".
 * Decided from the quote (`alreadyPaid`), the same answer that skips the
 * payment step, so the words and the flow cannot disagree.
 */
export const PAID_RECOVERY_MESSAGE =
  'That time is no longer available. Your payment is safe — pick a new time and we’ll book it. You won’t be charged again.';

export function recoveryBanner(message: string, alreadyPaid: boolean | undefined): string {
  return alreadyPaid === true ? PAID_RECOVERY_MESSAGE : message;
}

/**
 * D71 — the server's card requirement disagreed with the quote the widget
 * holds. `PAYMENT_REQUIRED` (checkout): a card is needed and none was saved.
 * `CARD_NOT_REQUIRED` (payment): a card was offered for a cart that owes
 * none. Both are fixed the same way — fetch a fresh quote and route by it —
 * and neither is a Time-step problem, so they stay out of
 * checkoutRecoveryMessage.
 */
export function isCardStateMismatch(code: string | undefined): boolean {
  return code === 'PAYMENT_REQUIRED' || code === 'CARD_NOT_REQUIRED';
}

/** Read the `?service=<id>` deep-link from the current URL (browser only). */
function readPreselectFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('service');
}

export class WidgetApi {
  constructor(
    private readonly baseUrl: string,
    private readonly slug: string,
  ) {}

  private url(path: string): string {
    return `${this.baseUrl}/api/widget/${encodeURIComponent(this.slug)}${path}`;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(this.url(path), {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'omit',
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({ error: { code: 'UNKNOWN' } }))) as {
        error?: { code: string; message?: string };
      };
      throw new WidgetApiError(
        err.error?.code ?? 'UNKNOWN',
        err.error?.message ?? res.statusText,
        res.status,
      );
    }
    return (await res.json()) as T;
  }

  /**
   * Phase 7 Commit H. The bootstrap endpoint accepts a widget slug (the
   * canonical multi-instance identifier). The server still understands
   * `?theme=` as a backwards-compat alias for the same parameter, so
   * existing embeds keep working through the migration window.
   *
   * Phase 5 — read-through cache (5-min TTL, stale-while-revalidate).
   *   - Fresh hit:   return cached payload, no network.
   *   - Stale hit:   return cached payload, kick off a background refresh.
   *                  If the refreshed payload differs, `onRefresh` fires so
   *                  App.tsx can hot-swap the catalog mid-session (or skip
   *                  if user has already started a cart).
   *   - Cold:        fetch, cache, return.
   */
  async bootstrap(opts?: {
    widget?: string | null;
    onRefresh?: (fresh: BootstrapResponse) => void;
    preselect?: string | null;
  }): Promise<BootstrapResponse> {
    const widget = opts?.widget ?? null;
    // A `?service=` deep-link produces a bootstrap that carries an extra
    // UNLISTED service — so it MUST be part of the cache key, or a plain load's
    // cache would be served to the deep-link (dropping the unlisted service and
    // breaking the preselect), and vice-versa.
    //
    // The caller (App) passes the deep-link's preselectedServiceId, which
    // covers BOTH entry paths — URL `?service=` and programmatic
    // `SalonLift.open({ service })`. The URL read is only a fallback for
    // callers that don't thread the deep link through.
    const preselect = opts?.preselect ?? readPreselectFromUrl();
    const key = cacheKey(this.slug, widget, null, preselect);
    const entry = getCached(key);
    if (entry && !isStale(entry)) {
      return entry.data;
    }
    if (entry && isStale(entry)) {
      // Stale-while-revalidate. Serve immediately, refresh in background.
      // Failures here are swallowed — next open() will retry; the user
      // sees stale data, not an error.
      void this.fetchBootstrap(widget, preselect)
        .then((fresh) => {
          setCached(key, fresh);
          if (opts?.onRefresh && bootstrapDiffers(entry.data, fresh)) {
            opts.onRefresh(fresh);
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[SalonLift] bootstrap refresh failed', e);
        });
      return entry.data;
    }
    const fresh = await this.fetchBootstrap(widget, preselect);
    setCached(key, fresh);
    return fresh;
  }

  private fetchBootstrap(
    widget: string | null,
    preselect: string | null,
  ): Promise<BootstrapResponse> {
    const params = new URLSearchParams();
    if (widget) params.set('w', widget);
    // Forward a deep-linked `?service=<id>` so the server can surface an
    // UNLISTED (menu-hidden) service for direct-link booking.
    if (preselect) params.set('preselect', preselect);
    const qs = params.toString();
    return this.request('GET', `/bootstrap${qs ? `?${qs}` : ''}`);
  }

  createCart(locationId: string): Promise<Cart> {
    return this.request('POST', '/cart', { locationId });
  }

  getCart(cartId: string): Promise<Cart> {
    return this.request('GET', `/cart/${encodeURIComponent(cartId)}`);
  }

  addService(
    cartId: string,
    input: { serviceId: string; staffId?: string; allowUnlisted?: boolean; unlistedToken?: string },
  ): Promise<Cart> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/service`, input);
  }

  removeService(cartId: string, lineId: string): Promise<Cart> {
    return this.request(
      'DELETE',
      `/cart/${encodeURIComponent(cartId)}/service/${encodeURIComponent(lineId)}`,
    );
  }

  availableDates(
    cartId: string,
    start: string,
    end: string,
    tz: string,
  ): Promise<{ dates: string[] }> {
    const qs = new URLSearchParams({ start, end, tz }).toString();
    return this.request('GET', `/cart/${encodeURIComponent(cartId)}/availability?${qs}`);
  }

  availableTimes(cartId: string, date: string, tz: string): Promise<{ slots: AvailableSlot[] }> {
    const qs = new URLSearchParams({ date, tz }).toString();
    return this.request('GET', `/cart/${encodeURIComponent(cartId)}/times?${qs}`);
  }

  reserveSlot(cartId: string, slotId: string): Promise<Cart> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/reserve`, {
      slotId,
    });
  }

  setClient(
    cartId: string,
    client: { givenName: string; familyName?: string; email?: string; phone: string },
  ): Promise<Cart> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/client`, client);
  }

  checkout(
    cartId: string,
    idempotencyKey: string,
    attribution?: {
      clickIds?: Record<string, string>;
      utms?: Record<string, string>;
      landingPageUrl?: string;
      referrerUrl?: string;
      firetrackVisitorId?: string;
    },
  ): Promise<Booking> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/checkout`, {
      idempotencyKey,
      ...(attribution ? { attribution } : {}),
    });
  }

  /**
   * Apply a promo code. Resolves with `ok: false` and a message for a
   * code the salon rejects — a mistyped promo is a normal thing for a
   * customer to do, so the server answers 200 and the widget shows the
   * message inline rather than treating it as a failure.
   */
  applyDiscount(
    cartId: string,
    code: string,
  ): Promise<
    | { ok: true; code: string; discountCents: number; subtotalCents: number }
    | { ok: false; message: string }
  > {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/discount`, { code });
  }

  removeDiscount(cartId: string): Promise<{ ok: boolean; subtotalCents: number }> {
    return this.request('DELETE', `/cart/${encodeURIComponent(cartId)}/discount`);
  }

  /**
   * Choose deposit vs pay-in-full. Sends the CHOICE only — the amount is
   * derived server-side from the cart (D68), which is why the response
   * carries the figure back rather than the request carrying one in.
   */
  setPaymentMode(
    cartId: string,
    payInFull: boolean,
  ): Promise<{
    ok: boolean;
    payInFull: boolean;
    amountCents: number;
    fullAmountCents: number;
    currency: string;
  }> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/payment-mode`, {
      payInFull,
    });
  }

  getDeposit(cartId: string): Promise<DepositInfo> {
    return this.request('GET', `/cart/${encodeURIComponent(cartId)}/deposit`);
  }

  attachPayment(
    cartId: string,
    sourceId: string,
    opts?: {
      // sb-vault card-on-file: non-PAN display metadata + customer consent.
      // The Stripe path sends `policyAccepted` too — the opt-in to keep the
      // card for future visits — but never `cardMeta`, since Stripe holds
      // the card itself. Square and Boulevard pass neither.
      cardMeta?: {
        brand?: string;
        last4?: string;
        expMonth?: number;
        expYear?: number;
      };
      policyAccepted?: boolean;
      /**
       * The save-card wording that was on screen when the tick was given.
       * The server compares it with its own and refuses on a mismatch; it
       * never stores or echoes this text.
       */
      policyTextSeen?: string;
    },
    // Square/Stripe/Boulevard return the refreshed cart; the sb-vault
    // card-on-file path returns only { paymentAttached: true } (no cart —
    // storing a card doesn't mutate cart contents). Callers narrow on `id`.
  ): Promise<Cart | { paymentAttached: boolean }> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/payment`, {
      sourceId,
      ...(opts?.policyAccepted !== undefined ? { policyAccepted: opts.policyAccepted } : {}),
      ...(opts?.policyTextSeen !== undefined ? { policyTextSeen: opts.policyTextSeen } : {}),
      ...(opts?.cardMeta ? { cardMeta: opts.cardMeta } : {}),
    });
  }

  /**
   * Create the PaymentIntent for a deferred Payment Element (P5).
   *
   * Sends no amount and no payment method. The amount is recomputed
   * server-side from the cart — a money endpoint takes no money input
   * from a browser (D68) — and the server refuses anything else.
   */
  createPaymentIntent(
    cartId: string,
    attribution?: {
      clickIds?: Record<string, string>;
      utms?: Record<string, string>;
      landingPageUrl?: string;
      referrerUrl?: string;
      firetrackVisitorId?: string;
    },
    /** The page being paid on, for a redirect payment's way back. */
    returnTo?: string,
    /**
     * Card saving (D73): what the Element holds and the wording beside the
     * box. Not money — whether a card is kept is the server's decision.
     */
    saveCard?: { accepted: boolean; policyTextSeen: string | null },
  ): Promise<{
    clientSecret: string;
    paymentIntentId: string;
    amountCents: number;
    returnUrl?: string;
  }> {
    return this.request('POST', `/cart/${encodeURIComponent(cartId)}/payment-intent`, {
      ...(attribution ? { attribution } : {}),
      ...(returnTo ? { returnTo } : {}),
      ...(saveCard ? { saveCard } : {}),
    });
  }

  /** Phase 6 — start (or, with the same key, return) a Special Services order. */
  createOrder(body: {
    orderKey: string;
    productId: string;
    client: { givenName: string; familyName?: string | null; email: string; phone: string };
    attribution?: Record<string, unknown>;
    turnstileToken?: string;
  }): Promise<{ order: ShopOrder }> {
    return this.request('POST', '/orders', body);
  }

  /** Phase 6 — the order's payment intent, or `paid` when it already is. */
  createOrderIntent(
    orderId: string,
  ): Promise<{ clientSecret: string; paymentIntentId: string } | { paid: true; order: ShopOrder }> {
    return this.request('POST', `/orders/${encodeURIComponent(orderId)}/payment-intent`);
  }

  /** Phase 6 — ask the server to confirm the order with Stripe. */
  completeOrder(orderId: string): Promise<{ order: ShopOrder }> {
    return this.request('POST', `/orders/${encodeURIComponent(orderId)}/complete`);
  }

  /**
   * P5.2 D — spend a resume token from a payment redirect's return URL and
   * get the customer's own cart back. Rejects (404 RESUME_INVALID) for an
   * unknown, used or expired token — the caller then starts fresh.
   */
  resumeCart(token: string): Promise<{ cart: Cart }> {
    return this.request('POST', '/cart/resume', { token });
  }

  abandon(cartId: string): Promise<{ ok: true }> {
    return this.request('DELETE', `/cart/${encodeURIComponent(cartId)}`);
  }
}
